import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Page";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Factory, Package, FileText, FileCheck2, BadgeCheck, Plus, ArrowRight, Info, Check, Circle } from "lucide-react";
import { CreateEpdDialog } from "@/pages/Epds";

const KPIS = [
  { key: "plants", label: "Plants", icon: Factory },
  { key: "products", label: "Products", icon: Package },
  { key: "draft_epds", label: "EPDs in preparation", icon: FileText },
  { key: "ready_for_review", label: "Ready for Review", icon: FileCheck2 },
  { key: "issued_epds", label: "Issued EPDs", icon: BadgeCheck },
];

function GettingStarted({ setup, onCreateEpd }) {
  const nav = useNavigate();
  const items = [
    { done: true, title: "Organisation account", body: "Your workspace is ready.", cta: null },
    { done: setup.profile, title: "Complete organisation profile", body: "Add your logo, sustainability contact and EPD contact.", label: "Complete profile", to: () => nav("/onboarding") },
    { done: setup.plants > 0, title: "Add your first plant", body: "Set up the manufacturing facility covered by your EPD.", label: "Add plant", to: () => nav("/app/plants") },
    { done: setup.products > 0, title: "Add your first product", body: "Choose Cement or Ready-Mix Concrete and define the product.", label: "Add product", to: () => nav("/app/products") },
    { done: setup.epds > 0, title: "Create your first EPD", body: "Start the declaration workflow.", label: "Create EPD", to: onCreateEpd },
  ];
  return (
    <div className="bg-white border rounded-lg p-6 mb-8" data-testid="getting-started">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium">Welcome to EPD Nigeria</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Let's set up your organisation and create your first Environmental Product Declaration.</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-semibold text-slate-900" data-testid="setup-percent">{setup.percent}%</div>
          <div className="text-xs text-muted-foreground">Setup progress</div>
        </div>
      </div>
      <div className="mt-5 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border p-3">
            {it.done ? <Check className="h-5 w-5 text-emerald-600 shrink-0" /> : <Circle className="h-5 w-5 text-slate-300 shrink-0" />}
            <div className="flex-1">
              <div className={`text-sm font-medium ${it.done ? "text-slate-500 line-through" : "text-slate-800"}`}>{i + 1}. {it.title}</div>
              {!it.done && <div className="text-xs text-muted-foreground">{it.body}</div>}
            </div>
            {!it.done && it.label && <Button size="sm" variant="outline" data-testid={`gs-step-${i}`} onClick={it.to}>{it.label} <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const load = () => api.get("/dashboard").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const rows = data?.rows || [];
  const setup = user?.setup || { percent: 0, complete: true, profile: false, plants: 0, products: 0, epds: 0 };
  const showChecklist = !setup.complete || showGuide;

  return (
    <div>
      <PageHeader title="Overview" subtitle="Your organisation's EPD programme at a glance."
        action={<Button data-testid="create-epd-cta" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Create EPD</Button>} />

      {showChecklist && <GettingStarted setup={setup} onCreateEpd={() => setOpen(true)} />}
      {setup.complete && !showGuide && (
        <button className="text-xs text-primary hover:underline mb-4" data-testid="view-setup-guide" onClick={() => setShowGuide(true)}>View setup guide</button>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {KPIS.map((k) => (
          <div key={k.key} data-testid={`kpi-${k.key}`} className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{k.label}</span>
              <k.icon className="h-4 w-4 text-slate-400" />
            </div>
            <div className="font-mono text-3xl font-semibold mt-2 text-slate-900">
              {data ? (data.kpis[k.key] ?? 0) : "—"}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-5 py-3.5 border-b flex items-center justify-between">
          <h3 className="font-medium">Products & EPD status</h3>
        </div>
        {rows.length === 0 ? (
          <Empty icon={FileText} title="No EPD projects yet"
            hint="Create your first plant and product, then start an EPD to begin the COLLECT → CALCULATE → CHECK → GENERATE → PUBLISH journey."
            action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Create EPD</Button>} />
        ) : (
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="px-5 py-2.5">Product</th>
                <th className="px-5 py-2.5">Plant</th>
                <th className="px-5 py-2.5">Category</th>
                <th className="px-5 py-2.5">Data</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5">Updated</th>
                <th className="px-5 py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.epd_id} data-testid={`epd-row-${r.epd_id}`} className="border-b last:border-0">
                  <td className="px-5 py-3 font-medium text-slate-800">
                    {r.product}
                    {r.is_demo && <span className="ml-2 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{r.plant}</td>
                  <td className="px-5 py-3 text-slate-600">{r.category === "cement" ? "Cement" : "Ready-Mix"}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${r.data_completion}%` }} />
                      </div>
                      <span className="font-mono text-xs text-slate-500">{r.data_completion}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-3 text-slate-500 text-xs">{(r.updated_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`epd-open-${r.epd_id}`}
                      onClick={() => nav(`/app/epds/${r.epd_id}`)}>
                      {r.status === "ISSUED" ? "View EPD" : r.status === "REVIEW_READY" ? "Review" : "Continue"}
                      <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>KPI counts and statuses are derived live from your EPD projects. Nothing on this dashboard is hard-coded.</span>
      </div>

      <CreateEpdDialog open={open} onOpenChange={setOpen} onCreated={(id) => nav(`/app/epds/${id}`)} />
    </div>
  );
}
